import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ETypeOfNotification,
  ETypes,
  RColumn,
  RemoteTable,
  RemoteTableRef,
  setNotification,
  stringFilterTemplate,
} from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { ColumnFilterElementTemplateOptions } from 'primereact/column'
import { useDispatch } from 'react-redux'
import {
  ContractHonorariumBulkResultDTO,
  ContractHonorariumCreateResultDTO,
  ContractHonorariumListItemDTO,
  ContractHonorariumListQueryParams,
  ContractHonorariumOptionsDTO,
} from '../../dto/contractHonorarium/ContractHonorariumDTO'
import {
  bulkCloseContractHonorariums,
  bulkEditContractHonorariums,
  closeContractHonorarium,
  deleteContractHonorarium,
  getContractHonorariumOptions,
  getContractHonorariums,
} from '../../services/ContractHonorariumController'
import { parseApiError } from '../../services/apiError'
import { fromDateToString } from '../../utils/utils'
import LocationActionMenu, { LocationActionItem } from '../Location/tabs/components/LocationActionMenu'
import BulkCloseContractHonorariumModal from './components/BulkCloseContractHonorariumModal'
import BulkEditContractHonorariumModal, { BulkEditValues } from './components/BulkEditContractHonorariumModal'
import { describeBulkResult } from './components/bulkSummary'
import CloseContractHonorariumModal from './components/CloseContractHonorariumModal'
import ContractHonorariumHistoryModal from './components/ContractHonorariumHistoryModal'
import DeleteContractHonorariumModal from './components/DeleteContractHonorariumModal'
import StatusFilter from './components/StatusFilter'
import ContractHonorariumDetailView from './ContractHonorariumDetailView'
import ContractHonorariumFormView from './ContractHonorariumFormView'
import { contractKindLabel, formatIsoDate, STATUS_LABELS } from './contractHonorariumFormat'
import './ContractHonorarium.scss'

const TABLE_ID = 'ContractHonorariumTable'

const COLUMN_WIDTHS = {
  professional: 240,
  billingentityname: 220,
  healthentityandplan: 260,
  nomenclator: 280,
  status: 208,
  buildingname: 180,
}
const ROW_ACTIONS_WIDTH = 50
const SELECTION_WIDTH = 50
const TABLE_MIN_WIDTH = Object.values(COLUMN_WIDTHS).reduce((total, width) => total + width, ROW_ACTIONS_WIDTH + SELECTION_WIDTH)

const ROW_HEIGHT = 58
const FILTERS_TYPE = [{ field: 'status', type: ETypes.integer }]
const EMPTY_OPTIONS: ContractHonorariumOptionsDTO = {
  billingEntities: [],
  healthEntities: [],
  professionals: [],
  distributionTypes: [],
  buildings: [],
}

const STALE_ERROR_CODES = [
  'CONTRACT_HONORARIUM_NOT_FOUND',
  'CONTRACT_HONORARIUM_NOT_DELETABLE',
  'CONTRACT_HONORARIUM_NOT_CLOSABLE',
]

const BULK_ERROR_CODES = [
  'EMPTY_SELECTION',
  'CONTRACT_NOT_FOUND',
  'INVALID_VALUES',
  'INVALID_FROM_DATE',
  'INVALID_CLOSE_DATE',
]


function statusFilterElement(filterOptions: ColumnFilterElementTemplateOptions) {
  return <StatusFilter filterOptions={filterOptions} />
}

function isGeneral(value: string): boolean {
  return !value || value === '(General)'
}

function valueOrAll(value: string): string {
  return isGeneral(value) ? 'Todos' : value
}

function HealthEntityAndPlanCell({ row }: Readonly<{ row: ContractHonorariumListItemDTO }>) {
  const healthEntity = isGeneral(row.healthentityname) ? 'Todas las obras sociales' : row.healthentityname
  const plan = isGeneral(row.healthentityplanname) ? 'Todos los planes' : row.healthentityplanname
  return (
    <div className='chd-two-lines'>
      <span title={healthEntity}>{healthEntity}</span>
      <span className='chd-two-lines__second' title={plan}>{plan}</span>
    </div>
  )
}

function StatusAndValidityCell({ row }: Readonly<{ row: ContractHonorariumListItemDTO }>) {
  return (
    <div className='chd-two-lines chd-status-validity'>
      <span>{STATUS_LABELS[row.status]}</span>
      <div className='chd-date-range'>
        <span>{formatIsoDate(row.datestart)}</span>
        <span>-</span>
        <span>{row.dateend ? formatIsoDate(row.dateend) : ''}</span>
      </div>
    </div>
  )
}

function hasFilters(params: ContractHonorariumListQueryParams): boolean {
  const filterDTO = params.filterDTO
  if (typeof filterDTO !== 'object' || filterDTO === null) return false
  const filters = (filterDTO as { filters?: unknown }).filters
  return Array.isArray(filters) && filters.length > 0
}

function describeCreation(result: ContractHonorariumCreateResultDTO): string {
  return result.created === 1 ? 'Distribución creada' : `${result.created} distribuciones creadas`
}

export default function ContractHonorariumView() {
  const dispatch = useDispatch()
  const tableRef = useRef<RemoteTableRef>(null)

  const [options, setOptions] = useState<ContractHonorariumOptionsDTO>(EMPTY_OPTIONS)
  const [filtersApplied, setFiltersApplied] = useState(false)
  const [selectedRows, setSelectedRows] = useState<ContractHonorariumListItemDTO[]>([])
  const [bulkEditVisible, setBulkEditVisible] = useState(false)
  const [bulkCloseVisible, setBulkCloseVisible] = useState(false)

  const [formVisible, setFormVisible] = useState(false)
  const [rowToEdit, setRowToEdit] = useState<ContractHonorariumListItemDTO | null>(null)
  const [rowToView, setRowToView] = useState<ContractHonorariumListItemDTO | null>(null)

  const [rowToDelete, setRowToDelete] = useState<ContractHonorariumListItemDTO | null>(null)
  const [rowToClose, setRowToClose] = useState<ContractHonorariumListItemDTO | null>(null)
  const [closeDateError, setCloseDateError] = useState<string | undefined>()
  const [rowForHistory, setRowForHistory] = useState<ContractHonorariumListItemDTO | null>(null)
  const [working, setWorking] = useState(false)

  const notifySuccess = useCallback((title: string) => {
    dispatch(setNotification({
      title,
      typeOfNotification: ETypeOfNotification.INFO,
      icon: 'pi-check-circle',
      duration: 5000,
    }))
  }, [dispatch])

  const notifyError = useCallback((title: string) => {
    dispatch(setNotification({
      title,
      typeOfNotification: ETypeOfNotification.ERROR,
      icon: 'pi-times-circle',
      duration: 8000,
    }))
  }, [dispatch])

  useEffect(() => {
    let cancelled = false
    getContractHonorariumOptions()
      .then((loaded) => {
        if (!cancelled) setOptions(loaded)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        parseApiError(error)
        notifyError('No se pudieron cargar las opciones del formulario. Intentá de nuevo.')
      })
    return () => {
      cancelled = true
    }
  }, [notifyError])

  const loadRows = useCallback(
    (offSet: number, pageSize: number, params: ContractHonorariumListQueryParams = {}) => {
      if (pageSize > 0) {
        setFiltersApplied(hasFilters(params))
        if (offSet === 0) setSelectedRows((current) => (current.length === 0 ? current : []))
      }
      return getContractHonorariums(offSet, pageSize, params).catch((error: unknown) => {
        parseApiError(error)
        notifyError('No se pudieron cargar las distribuciones. Intentá de nuevo.')
        return [] as ContractHonorariumListItemDTO[]
      })
    },
    [notifyError],
  )

  function refresh() {
    tableRef.current?.refreshTable?.()
  }

  function openCreate() {
    setRowToEdit(null)
    setFormVisible(true)
  }

  const openEdit = useCallback((row: ContractHonorariumListItemDTO) => {
    setRowToView(null)
    setRowToEdit(row)
    setFormVisible(true)
  }, [])

  function closeForm() {
    setFormVisible(false)
    setRowToEdit(null)
  }

  function handleCreated(result: ContractHonorariumCreateResultDTO) {
    closeForm()
    notifySuccess(describeCreation(result))
    refresh()
  }

  function handleUpdated(updated: boolean) {
    closeForm()
    notifySuccess(updated ? 'Cambios guardados' : 'No había cambios para guardar')
    if (updated) refresh()
  }

  function handleStale(message: string) {
    closeForm()
    setRowToView(null)
    notifyError(message)
    refresh()
  }

  function notifyActionError(error: unknown, fallback: string) {
    const apiError = parseApiError(error)
    notifyError(STALE_ERROR_CODES.includes(apiError.errorCode) ? apiError.message : fallback)
  }

  async function confirmDelete() {
    if (!rowToDelete || working) return
    setWorking(true)
    try {
      await deleteContractHonorarium(rowToDelete.contracthonorariumid)
      notifySuccess('Distribución eliminada')
    } catch (error: unknown) {
      notifyActionError(error, 'No se pudo eliminar la distribución. Intentá de nuevo.')
    } finally {
      setWorking(false)
      setRowToDelete(null)
      refresh()
    }
  }

  async function confirmClose(closeDate: Date) {
    if (!rowToClose || working) return
    setWorking(true)
    setCloseDateError(undefined)
    try {
      await closeContractHonorarium(rowToClose.contracthonorariumid, fromDateToString(closeDate))
      notifySuccess('Fecha de cierre agregada')
      setRowToClose(null)
      refresh()
    } catch (error: unknown) {
      const apiError = parseApiError(error)
      const dateError = apiError.details?.find((detail) => detail.field === 'closeDate')?.message
      if (dateError) {
        setCloseDateError(dateError)
      } else {
        notifyActionError(error, 'No se pudo agregar la fecha de cierre. Intentá de nuevo.')
        setRowToClose(null)
        refresh()
      }
    } finally {
      setWorking(false)
    }
  }

  function notifyBulkResult(result: ContractHonorariumBulkResultDTO) {
    if (result.skipped === 0) {
      notifySuccess(describeBulkResult(result))
      return
    }
    dispatch(setNotification({
      title: describeBulkResult(result),
      typeOfNotification: ETypeOfNotification.WARNING,
      icon: 'pi-exclamation-triangle',
      duration: 8000,
    }))
  }

  function notifyBulkError(error: unknown, fallback: string) {
    const apiError = parseApiError(error)
    notifyError(BULK_ERROR_CODES.includes(apiError.errorCode) ? apiError.message : fallback)
  }

  async function confirmBulkEdit(values: BulkEditValues) {
    if (working || selectedRows.length === 0) return
    setWorking(true)
    try {
      const result = await bulkEditContractHonorariums({
        ids: selectedRows.map((row) => row.contracthonorariumid),
        distributionTypeId: values.distributionTypeId,
        honoPercentage: values.honoPercentage,
        copaymentPercentage: values.copaymentPercentage,
        diferentialPercentage: values.diferentialPercentage,
        fixedAmount: values.fixedAmount,
        fromDate: values.fromDate ? fromDateToString(values.fromDate) : null,
      })
      setBulkEditVisible(false)
      notifyBulkResult(result)
      refresh()
    } catch (error: unknown) {
      notifyBulkError(error, 'No se pudieron modificar las distribuciones. Intentá de nuevo.')
    } finally {
      setWorking(false)
    }
  }

  async function confirmBulkClose(closeDate: Date) {
    if (working || selectedRows.length === 0) return
    setWorking(true)
    try {
      const result = await bulkCloseContractHonorariums({
        ids: selectedRows.map((row) => row.contracthonorariumid),
        closeDate: fromDateToString(closeDate),
      })
      setBulkCloseVisible(false)
      notifyBulkResult(result)
      refresh()
    } catch (error: unknown) {
      notifyBulkError(error, 'No se pudo agregar la fecha de cierre. Intentá de nuevo.')
    } finally {
      setWorking(false)
    }
  }

  const rowActions = useCallback((row: ContractHonorariumListItemDTO) => {
    const menuItems: LocationActionItem[] = [
      { label: 'Ver detalle', icon: 'pi pi-eye', action: () => setRowToView(row) },
      { label: 'Editar', icon: 'pi pi-pencil', action: () => openEdit(row) },
      {
        label: 'Agregar fecha de cierre',
        icon: 'pi pi-calendar-times',
        action: () => {
          setCloseDateError(undefined)
          setRowToClose(row)
        },
        disabled: row.status !== 'CURRENT' || row.dateend != null,
      },
      {
        label: 'Eliminar',
        icon: 'pi pi-trash',
        action: () => setRowToDelete(row),
        disabled: row.status !== 'FUTURE',
      },
      {
        label: 'Ver historial',
        icon: 'pi pi-history',
        action: () => setRowForHistory(row),
        disabled: row.status === 'FUTURE',
      },
    ]

    return (
      <LocationActionMenu
        items={menuItems}
        ariaLabel='Opciones de la distribución'
        buttonId={`chdMenu_${row.contracthonorariumid}`}
        buttonClassName='p-button-text nv-icon'
        panelClassName='chd-action-menu-panel'
      />
    )
  }, [openEdit])

  const bulkToolbar = (
    <div className='chd-toolbar'>
      <span className='chd-selection-count' aria-live='polite'>
        {selectedRows.length === 0 ? '' : `${selectedRows.length} contratos seleccionados`}
      </span>
      <div className={`chd-bulk-actions${selectedRows.length === 0 ? ' chd-keep-space-hidden' : ''}`}>
        <Button
          type='button'
          className='buttonMain2'
          outlined
          label='Edición en bloque'
          onClick={() => setBulkEditVisible(true)}
        />
        <Button
          type='button'
          className='buttonMain2'
          outlined
          label='Agregar fecha de cierre'
          onClick={() => setBulkCloseVisible(true)}
        />
      </div>
    </div>
  )

  if (formVisible) {
    return (
      <ContractHonorariumFormView
        key={rowToEdit?.contracthonorariumid ?? 'new'}
        contractHonorariumId={rowToEdit?.contracthonorariumid ?? null}
        options={options}
        onBack={closeForm}
        onCreated={handleCreated}
        onUpdated={handleUpdated}
        onError={notifyError}
        onStale={handleStale}
      />
    )
  }

  if (rowToView) {
    return (
      <ContractHonorariumDetailView
        key={rowToView.contracthonorariumid}
        contractHonorariumId={rowToView.contracthonorariumid}
        distributionTypes={options.distributionTypes}
        onBack={() => setRowToView(null)}
        onEdit={() => openEdit(rowToView)}
        onError={notifyError}
        onStale={handleStale}
      />
    )
  }

  return (
    <div className='nv-scope nv-page chd-view'>
      <div className='chd-header'>
        <h1 className='nv-h1'>Distribución de honorarios</h1>
        <Button
          id='buttonCreateContractHonorarium'
          type='button'
          className='buttonMain'
          label='Crear'
          icon='pi pi-plus-circle'
          onClick={openCreate}
        />
      </div>

      <RemoteTable
        ref={tableRef}
        idTable={TABLE_ID}
        dataKey='contracthonorariumid'
        className='chd-table'
        headerClassName='chd-table-header'
        headerLeft={bulkToolbar}
        getItems={loadRows}
        filterRow
        resizableColumns
        tableMinWidth={TABLE_MIN_WIDTH}
        itemSize={ROW_HEIGHT}
        filtersType={FILTERS_TYPE}
        notFoundText={filtersApplied ? 'No hay resultados para estos filtros.' : 'Todavía no hay distribuciones cargadas.'}
        rowActions={rowActions}
        selectionMode='checkbox'
        selectedItems={selectedRows}
        onSelectionChange={(rows: ContractHonorariumListItemDTO[]) => setSelectedRows(rows)}
      >
        <RColumn
          field='professional'
          header='Profesional'
          grow={COLUMN_WIDTHS.professional}
          sortable
          filter
          filterElement={stringFilterTemplate}
          body={(row: ContractHonorariumListItemDTO) => valueOrAll(row.professional)}
        />
        <RColumn
          field='billingentityname'
          header='Emisor'
          grow={COLUMN_WIDTHS.billingentityname}
          sortable
          filter
          filterElement={stringFilterTemplate}
          body={(row: ContractHonorariumListItemDTO) => (
            <span className='chd-issuer'>
              {valueOrAll(row.billingentityname)}
              {/* Con un profesional como emisor, lo que no se queda el profesional se le retiene */}
              {row.billingEntityProfessional && <span className='md-tag md-tag-mut'>{contractKindLabel(true)}</span>}
            </span>
          )}
        />
        <RColumn
          field='healthentityandplan'
          header='Obra social - Plan'
          grow={COLUMN_WIDTHS.healthentityandplan}
          sortable
          filter
          filterElement={stringFilterTemplate}
          body={(row: ContractHonorariumListItemDTO) => <HealthEntityAndPlanCell row={row} />}
        />
        <RColumn
          field='nomenclator'
          header='Nomenclador'
          grow={COLUMN_WIDTHS.nomenclator}
          sortable
          filter
          filterElement={stringFilterTemplate}
          body={(row: ContractHonorariumListItemDTO) => valueOrAll(row.nomenclator)}
        />
        <RColumn
          field='status'
          header='Vigencia'
          grow={COLUMN_WIDTHS.status}
          className='text-center'
          filter
          filterElement={statusFilterElement}
          body={(row: ContractHonorariumListItemDTO) => <StatusAndValidityCell row={row} />}
        />
        <RColumn
          field='buildingname'
          header='Edificio'
          grow={COLUMN_WIDTHS.buildingname}
          sortable
          filter
          filterElement={stringFilterTemplate}
          body={(row: ContractHonorariumListItemDTO) => valueOrAll(row.buildingname)}
        />
      </RemoteTable>

      <DeleteContractHonorariumModal
        visible={rowToDelete != null}
        contractHonorarium={rowToDelete}
        confirming={working}
        onCancel={() => setRowToDelete(null)}
        onConfirm={() => { void confirmDelete() }}
      />

      <CloseContractHonorariumModal
        visible={rowToClose != null}
        contractHonorarium={rowToClose}
        confirming={working}
        apiError={closeDateError}
        onCancel={() => setRowToClose(null)}
        onConfirm={(closeDate) => { void confirmClose(closeDate) }}
      />

      <ContractHonorariumHistoryModal
        contractHonorarium={rowForHistory}
        onClose={() => setRowForHistory(null)}
        onError={notifyError}
      />

      <BulkEditContractHonorariumModal
        visible={bulkEditVisible}
        count={selectedRows.length}
        distributionTypes={options.distributionTypes}
        confirming={working}
        onCancel={() => setBulkEditVisible(false)}
        onConfirm={(values) => { void confirmBulkEdit(values) }}
      />

      <BulkCloseContractHonorariumModal
        visible={bulkCloseVisible}
        count={selectedRows.length}
        confirming={working}
        onCancel={() => setBulkCloseVisible(false)}
        onConfirm={(closeDate) => { void confirmBulkClose(closeDate) }}
      />
    </div>
  )
}
